import React, { useState, useEffect, useRef } from 'react'
import { CustomPicker } from 'react-color'
import { Saturation, Hue, Alpha } from 'react-color/lib/components/common'
import SiteVars from '~/Classes/siteVars'
import i18n from '~/i18n'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TabsSimple from '~/Common-Objects/Tabs/TabsSimple'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import { useSelector } from 'react-redux'
import { withTransparentColour, rgbToHex, hexToRgb } from './colorPickerUtils'

function restoreEditorSelection(editorApp) {
    if (!editorApp) return
    if (typeof editorApp.restoreSelection === 'function') editorApp.restoreSelection()
    else if (editorApp.editor && typeof editorApp.editor.restore === 'function') editorApp.editor.restore()
}

function saveEditorSelection(editorApp) {
    if (!editorApp) return
    if (typeof editorApp.saveSelection === 'function') editorApp.saveSelection()
    else if (editorApp.editor && typeof editorApp.editor.save === 'function') editorApp.editor.save()
}

const defaultColors = [
    '#FFFFFF',
    '#EBF2FE',
    '#F7EDFD',
    '#FCEDF7',
    '#FCECEA',
    '#FDF4DF',
    '#FCF8CC',
    '#F2FFDB',
    '#E4FEF2',
    '#EAF7FE',
    '#DDDFE2',
    '#D2E0FA',
    '#EAD8F8',
    '#F6D2EA',
    '#F7D7D4',
    '#F8E5AD',
    '#F2E995',
    '#D9F0AF',
    '#C5F1DC',
    '#CEECFA',
    '#8D8F97',
    '#719DEB',
    '#BE80ED',
    '#D87BB8',
    '#E8796E',
    '#F0AB3C',
    '#EAD351',
    '#9FC55A',
    '#71CA9B',
    '#80C2DE',
    '#6B6E75',
    '#3166D5',
    '#8D4FB9',
    '#A24D84',
    '#B94235',
    '#B16022',
    '#8D6F23',
    '#637E33',
    '#42835F',
    '#3E7B99',
    '#1F1F20',
    '#2957B6',
    '#7742A1',
    '#894371',
    '#A0382C',
    '#944F1B',
    '#7A5F1D',
    '#546B2C',
    '#386C51',
    '#366980'
]

export default function ColorPickerComponent2025({
    color = null,
    onChange = () => {},
    globalColours = [],
    favoriteColors = [],
    defaultColorFormat = 'hex',
    editorApp = null,
    standalone = true,
    allowAlpha = false
}) {
    const textInputClickHandler = (e) => {
        e.stopPropagation()
    }

    const textInputChangeHandler = (e, hexColor) => {
        e.stopPropagation()
        if (editorApp) {
            restoreEditorSelection(editorApp)
            setHexColor(hexColor)
            onChange(hexColor, true)
            saveEditorSelection(editorApp)
            e.target.focus()
        } else {
            setHexColor(hexColor)
            onChange(hexColor, true)
        }
    }

    const lastDragKeyRef = useRef('')

    const draggableUpdateColor = (val) => {
        const payload = val && typeof val === 'object' && val.hex ? val : val
        const hexVal = payload && typeof payload === 'object' ? payload.hex : payload
        if (!hexVal) return
        // Hex stays the same while the alpha slider moves. Compare alpha too, or that drag is dropped.
        const nextKey = dragColorKey(allowAlpha ? payload : hexVal, allowAlpha)
        if (nextKey && nextKey === lastDragKeyRef.current) return
        lastDragKeyRef.current = nextKey
        if (editorApp) {
            restoreEditorSelection(editorApp)
            setHexColor(hexVal)
            onChange(allowAlpha ? payload : hexVal, true)
            saveEditorSelection(editorApp)
        } else {
            setHexColor(hexVal)
            onChange(allowAlpha ? payload : hexVal, true)
        }
    }

    const account = useSelector((state) => state.accountMaster)
    const [selectedTab, setSelectedTab] = useState(0)
    const [colorFormat, setColorFormat] = useState(defaultColorFormat)
    const [hexColor, setHexColor] = useState('')
    useEffect(() => {
        if (color == hexColor) return
        setHexColor(rgbToHex(color))
    }, [color])

    const accountColours = account.accountMaster.options ? account.accountMaster.options.colours || [] : []
    const brandAnalyzerColours = account.accountMaster.options
        ? account.accountMaster.options.brandAnalyzer?.colours.primary
            ? [
                  account.accountMaster.options.brandAnalyzer.colours.primary,
                  account.accountMaster.options.brandAnalyzer.colours.text,
                  account.accountMaster.options.brandAnalyzer.colours.background
              ]
            : account.accountMaster.options.brandAnalyzer?.colours || []
        : []

    let classNames = 'mpzColorPicker2025'
    if (standalone) classNames += ' mpzColorPicker2025-standalone'

    const globalColoursFinal = withTransparentColour(globalColours)

    return (
        <div className={classNames}>
            <TabsSimple
                tabs={[
                    { label: 'Saved', value: 0 },
                    { label: 'Custom', value: 1 }
                ]}
                selectedTab={selectedTab}
                onChange={(tab) => {
                    setSelectedTab(tab)
                }}
            />

            {selectedTab == 0 && (
                <div className='mpzColorPicker2025-saved mpzColorPicker2025-sectionHolder'>
                    {accountColours && accountColours.length > 0 && (
                        <>
                            <div className='mpzColorPicker2025-section'>
                                <ColorPalletGenerator
                                    title='Account Colours'
                                    selectedColor={hexColor}
                                    incomingColors={accountColours}
                                    onChangeHandler={(e) => onChange(e, false)}
                                />
                            </div>
                        </>
                    )}

                    {brandAnalyzerColours && brandAnalyzerColours.length > 0 && (
                        <>
                            <div className='mpzColorPicker2025-section'>
                                <ColorPalletGenerator
                                    title='Brand Colours'
                                    selectedColor={hexColor}
                                    incomingColors={brandAnalyzerColours}
                                    onChangeHandler={(e) => onChange(e, false)}
                                />
                            </div>
                        </>
                    )}

                    {globalColoursFinal && globalColoursFinal.length > 0 && (
                        <>
                            <div className='mpzColorPicker2025-section'>
                                <ColorPalletGenerator
                                    title='Template Colours'
                                    selectedColor={hexColor}
                                    incomingColors={globalColoursFinal}
                                    onChangeHandler={(e) => onChange(e, false)}
                                />
                            </div>
                        </>
                    )}

                    {favoriteColors && favoriteColors.length > 0 && (
                        <>
                            <div className='mpzColorPicker2025-section'>
                                <ColorPalletGenerator
                                    title={i18n.t('templates:add.drag.colours.favourite')}
                                    selectedColor={hexColor}
                                    incomingColors={favoriteColors}
                                    onChangeHandler={(e) => onChange(e, false)}
                                />
                            </div>
                        </>
                    )}

                    {defaultColors && defaultColors.length > 0 && (
                        <>
                            <div className='mpzColorPicker2025-section'>
                                <ColorPalletGenerator
                                    title='Default Colours'
                                    selectedColor={hexColor}
                                    incomingColors={defaultColors}
                                    onChangeHandler={(e) => onChange(e, false)}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
            {selectedTab == 1 && (
                <div className='mpzColorPicker2025-custom mpzColorPicker2025-sectionHolder'>
                    <CustomColorPicker
                        color={hexColor || color}
                        allowAlpha={allowAlpha}
                        onChange={(e) => {
                            draggableUpdateColor(allowAlpha ? e : e.hex)
                        }}
                        onChangeComplete={(e) => {
                            draggableUpdateColor(allowAlpha ? e : e.hex)
                        }}
                    />

                    <div className='mpzColorPicker2025-custom-colorHolder'>
                        <Select
                            data={[
                                { label: 'Hex', value: 'hex' },
                                { label: 'RGB', value: 'rgb' },
                                { label: 'HSL', value: 'hsl' },
                                { label: 'HSV', value: 'hsv' }
                            ]}
                            value={colorFormat}
                            onChange={(e) => {
                                setColorFormat(e.currentTarget.value)
                            }}
                        />

                        {colorFormat === 'hex' && (
                            <TextInput
                                name='colourHolder'
                                type='text'
                                placeHolder=''
                                value={hexColor}
                                onChange={(e) => textInputChangeHandler(e, e.target.value)}
                                onClick={textInputClickHandler}
                                onMouseDown={textInputClickHandler}
                                style={{ flexGrow: 1 }}
                                outerStyle={{ width: '100%' }}
                            />
                        )}

                        {colorFormat === 'rgb' && (
                            <>
                                <RGBComponent
                                    hexVal={hexColor}
                                    textInputChangeHandler={textInputChangeHandler}
                                    textInputClickHandler={textInputClickHandler}
                                />
                            </>
                        )}
                        {colorFormat === 'hsl' && (
                            <>
                                <HSLComponent
                                    hexVal={hexColor}
                                    textInputChangeHandler={textInputChangeHandler}
                                    textInputClickHandler={textInputClickHandler}
                                />
                            </>
                        )}
                        {colorFormat === 'hsv' && (
                            <>
                                <HSVComponent
                                    hexVal={hexColor}
                                    textInputChangeHandler={textInputChangeHandler}
                                    textInputClickHandler={textInputClickHandler}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function dragColorKey(value, allowAlpha) {
    if (value == null || value === '') return ''
    if (allowAlpha && typeof value === 'object') {
        const hex = value.hex ? String(value.hex).trim().toLowerCase() : ''
        const alpha = value.rgb && value.rgb.a != null ? String(value.rgb.a) : ''
        return `${hex}|${alpha}`
    }
    const hex = typeof value === 'object' ? value.hex : value
    return String(hex || '')
        .trim()
        .toLowerCase()
}

const SheetPointer = () => (
    <div
        style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.55)',
            transform: 'translate(-7px, -7px)',
            pointerEvents: 'none'
        }}
    />
)

const CustomColorPickerBase = ({ hsl, hsv, rgb, onChange, allowAlpha }) => {
    return (
        <>
            <div
                style={{
                    width: '100%',
                    height: '200px',
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}
            >
                <Saturation hsl={hsl} hsv={hsv} pointer={SheetPointer} onChange={onChange} />
            </div>
            <div
                style={{
                    width: '100%',
                    height: '12px',
                    position: 'relative',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    marginTop: '8px'
                }}
            >
                <Hue hsl={hsl} onChange={onChange} direction='horizontal' />
            </div>
            {allowAlpha && rgb && (
                <div
                    className='mpzColorPicker2025-alpha'
                    style={{
                        width: '100%',
                        height: '12px',
                        position: 'relative',
                        borderRadius: '999px',
                        overflow: 'hidden',
                        marginTop: '8px'
                    }}
                >
                    <Alpha rgb={rgb} hsl={hsl} onChange={onChange} />
                </div>
            )}
        </>
    )
}
const CustomColorPicker = CustomPicker(CustomColorPickerBase)

const ColorPalletGenerator = ({ title, selectedColor, incomingColors, onChangeHandler = () => {} }) => {
    return (
        <>
            <div className='mpzColorPicker2025-title'>{title}</div>
            <div className='mpzColorPicker2025-colorBlocks'>
                {incomingColors.map((theCol, index) => {
                    return (
                        <div
                            key={'col-' + index}
                            title={theCol}
                            className={
                                'mpzColorPicker2025-colorBlock' +
                                (selectedColor?.toLowerCase() === theCol?.toLowerCase()
                                    ? ' mpzColorPicker2025-colorBlock-selected'
                                    : '')
                            }
                            style={{
                                backgroundColor: theCol,
                                backgroundImage:
                                    theCol === 'transparent'
                                        ? 'url(' +
                                          SiteVars.repoEndPoint +
                                          'siteContent/assets/templates/common/empty-color.svg)'
                                        : 'none'
                            }}
                            onClick={() => {
                                onChangeHandler(theCol)
                            }}
                        />
                    )
                })}
            </div>
        </>
    )
}

const RGBComponent = ({ hexVal, textInputChangeHandler = () => {}, textInputClickHandler = () => {} }) => {
    const [rgbResult, setRgbResult] = useState(hexToRgb(hexVal))

    useEffect(() => {
        setRgbResult(hexToRgb(hexVal))
    }, [hexVal])

    const updateValue = (e, key) => {
        let tmpRgbResult = { ...rgbResult, [key]: e.target.value }
        setRgbResult(tmpRgbResult)
        const alpha = tmpRgbResult.a
        const nextColor =
            alpha != null && alpha < 1
                ? `rgba(${tmpRgbResult.r},${tmpRgbResult.g},${tmpRgbResult.b},${alpha})`
                : `rgb(${tmpRgbResult.r},${tmpRgbResult.g},${tmpRgbResult.b})`
        textInputChangeHandler(e, rgbToHex(nextColor))
    }

    return (
        <>
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={rgbResult.r}
                onChange={(e) => updateValue(e, 'r')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={rgbResult.g}
                onChange={(e) => updateValue(e, 'g')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={rgbResult.b}
                onChange={(e) => updateValue(e, 'b')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
        </>
    )
}

const HSLComponent = ({ hexVal, textInputChangeHandler = () => {}, textInputClickHandler = () => {} }) => {
    const [hslResult, setHslResult] = useState(hexToHsl(hexVal))

    useEffect(() => {
        setHslResult(hexToHsl(hexVal))
    }, [hexVal])

    const updateValue = (e, key) => {
        let tmpHslResult = { ...hslResult, [key]: e.target.value }
        setHslResult(tmpHslResult)
        textInputChangeHandler(e, hslToHex(tmpHslResult.h, tmpHslResult.s, tmpHslResult.l))
    }

    return (
        <>
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hslResult.h}
                onChange={(e) => updateValue(e, 'h')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hslResult.s}
                onChange={(e) => updateValue(e, 's')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hslResult.l}
                onChange={(e) => updateValue(e, 'l')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
        </>
    )
}

const HSVComponent = ({ hexVal, textInputChangeHandler = () => {}, textInputClickHandler = () => {} }) => {
    const [hsvResult, setHsvResult] = useState(hexToHsv(hexVal))

    useEffect(() => {
        setHsvResult(hexToHsv(hexVal))
    }, [hexVal])

    const updateValue = (e, key) => {
        let tmpHsvResult = { ...hsvResult, [key]: e.target.value }
        setHsvResult(tmpHsvResult)
        textInputChangeHandler(e, hsvToHex(tmpHsvResult.h, tmpHsvResult.s, tmpHsvResult.v))
    }

    return (
        <>
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hsvResult.h}
                onChange={(e) => updateValue(e, 'h')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hsvResult.s}
                onChange={(e) => updateValue(e, 's')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
            <TextInput
                name='colourHolder'
                type='text'
                placeHolder=''
                value={hsvResult.v}
                onChange={(e) => updateValue(e, 'v')}
                onClick={textInputClickHandler}
                onMouseDown={textInputClickHandler}
            />
        </>
    )
}

//HSL Helpers
function hexToHsl(hex) {
    hex = hex.replace(/^#/, '')

    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((ch) => ch + ch)
            .join('')
    }

    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h,
        s,
        l = (max + min) / 2

    if (max === min) {
        h = s = 0
    } else {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h /= 6
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
    }
}

function hslToHex(h, s, l) {
    s /= 100
    l /= 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r = 0,
        g = 0,
        b = 0

    if (0 <= h && h < 60) {
        r = c
        g = x
        b = 0
    } else if (60 <= h && h < 120) {
        r = x
        g = c
        b = 0
    } else if (120 <= h && h < 180) {
        r = 0
        g = c
        b = x
    } else if (180 <= h && h < 240) {
        r = 0
        g = x
        b = c
    } else if (240 <= h && h < 300) {
        r = x
        g = 0
        b = c
    } else if (300 <= h && h < 360) {
        r = c
        g = 0
        b = x
    }

    r = Math.round((r + m) * 255)
    g = Math.round((g + m) * 255)
    b = Math.round((b + m) * 255)

    const toHex = (v) => v.toString(16).padStart(2, '0')
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

//HSV Helpers
function hexToHsv(hex) {
    hex = hex.replace(/^#/, '')
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((ch) => ch + ch)
            .join('')
    }

    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    const d = max - min
    let h,
        s,
        v = max

    if (d === 0) {
        h = 0
    } else {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h *= 60
    }

    s = max === 0 ? 0 : d / max

    return {
        h: Math.round(h),
        s: Math.round(s * 100),
        v: Math.round(v * 100)
    }
}

function hsvToHex(h, s, v) {
    s /= 100
    v /= 100

    const c = v * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = v - c

    let r = 0,
        g = 0,
        b = 0

    if (0 <= h && h < 60) {
        r = c
        g = x
        b = 0
    } else if (60 <= h && h < 120) {
        r = x
        g = c
        b = 0
    } else if (120 <= h && h < 180) {
        r = 0
        g = c
        b = x
    } else if (180 <= h && h < 240) {
        r = 0
        g = x
        b = c
    } else if (240 <= h && h < 300) {
        r = x
        g = 0
        b = c
    } else if (300 <= h && h < 360) {
        r = c
        g = 0
        b = x
    }

    r = Math.round((r + m) * 255)
    g = Math.round((g + m) * 255)
    b = Math.round((b + m) * 255)

    const toHex = (v) => v.toString(16).padStart(2, '0')
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
